import { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Spin, message, Table, Tag, Progress, Alert, Button,
  Modal, Form, InputNumber, Input, Descriptions, Empty,
} from 'antd';
import { SettingOutlined, ThunderboltOutlined, WarningOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import { demand } from '../../../services/api';

function curveOption(curve) {
  if (!curve?.points?.length) return {};
  const markLines = [];
  if (curve.contract_kw > 0) {
    markLines.push({ yAxis: curve.contract_kw, lineStyle: { color: '#ff4d4f', type: 'dashed' }, label: { formatter: `合同需量 ${curve.contract_kw} kW` } });
  }
  if (curve.warn_threshold_kw) {
    markLines.push({ yAxis: curve.warn_threshold_kw, lineStyle: { color: '#faad14', type: 'dashed' }, label: { formatter: `预警线 ${curve.warn_threshold_kw} kW` } });
  }
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: ['功率', `滑窗需量(${curve.window_minutes}分钟)`] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: curve.points.map((p) => p.time) },
    yAxis: { type: 'value', name: 'kW' },
    series: [
      { name: '功率', type: 'line', showSymbol: false, data: curve.points.map((p) => p.power_kw), lineStyle: { color: '#1890ff', width: 1 }, areaStyle: { opacity: 0.08 } },
      {
        name: `滑窗需量(${curve.window_minutes}分钟)`, type: 'line', showSymbol: false,
        data: curve.points.map((p) => p.demand_kw), lineStyle: { color: '#fa8c16', width: 2 },
        markLine: { symbol: 'none', data: markLines },
      },
    ],
  };
}

function trendOption(trend) {
  if (!trend?.list?.length) return {};
  return {
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: trend.list.map((m) => m.month) },
    yAxis: { type: 'value', name: 'kW' },
    series: [{
      name: '最大需量', type: 'bar',
      data: trend.list.map((m) => m.max_demand_kw),
      itemStyle: { color: '#1890ff' },
      markLine: trend.contract_kw > 0 ? {
        symbol: 'none',
        data: [{ yAxis: trend.contract_kw, lineStyle: { color: '#ff4d4f', type: 'dashed' }, label: { formatter: `合同需量 ${trend.contract_kw}` } }],
      } : undefined,
    }],
  };
}

export default function DemandPage() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState(null);
  const [curve, setCurve] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm] = Form.useForm();
  const user = JSON.parse(localStorage.getItem('qhy_user') || '{}');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, td, cv] = await Promise.all([
        demand.overview(),
        demand.trend({ months: 6 }),
        demand.curve(),
      ]);
      setOverview(ov.data);
      setTrend(td.data);
      setCurve(cv.data);
    } catch (err) {
      message.error(err.message || '加载需量数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openSettings = async () => {
    try {
      const res = await demand.settings();
      settingsForm.setFieldsValue({
        contract_kw: res.data.contract_kw,
        window_minutes: res.data.window_minutes,
        warn_ratio: res.data.warn_ratio,
        price_per_kw: res.data.price_per_kw,
        meter_ids: (res.data.meter_ids || []).join(','),
      });
      setSettingsOpen(true);
    } catch (err) {
      message.error(err.message || '读取需量配置失败');
    }
  };

  const saveSettings = async () => {
    try {
      const values = await settingsForm.validateFields();
      await demand.updateSettings(values);
      message.success('需量配置已保存');
      setSettingsOpen(false);
      loadData();
    } catch (err) {
      if (err.message) message.error(err.message);
    }
  };

  if (loading && !overview) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!overview) return <div>暂无数据</div>;

  const noData = overview.data_source !== 'telemetry';
  const usagePercent = overview.usage_ratio != null ? Math.round(overview.usage_ratio * 1000) / 10 : 0;
  const ratioColor = overview.usage_ratio >= overview.config.warn_ratio ? '#ff4d4f' : overview.usage_ratio >= 0.75 ? '#faad14' : '#52c41a';
  const suggestion = overview.suggestion;

  const trendColumns = [
    { title: '月份', dataIndex: 'month' },
    { title: '最大需量(kW)', dataIndex: 'max_demand_kw', render: (v, row) => (v != null ? <b>{v}</b> : <Tag>暂无数据</Tag>) },
    { title: '发生时刻', dataIndex: 'occurred_at', render: (v) => (v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-') },
    {
      title: '占合同需量', dataIndex: 'max_demand_kw',
      render: (v) => {
        if (v == null || !trend?.contract_kw) return '-';
        const pct = Math.round((v / trend.contract_kw) * 1000) / 10;
        return <Progress percent={pct} size="small" strokeColor={pct >= overview.config.warn_ratio * 100 ? '#ff4d4f' : '#1890ff'} />;
      },
    },
    {
      title: '数据质量', dataIndex: 'low_confidence',
      render: (v, row) => {
        if (!row.has_data) return '-';
        return v ? <Tag color="orange">窗口覆盖不足(估算)</Tag> : <Tag color="green">正常</Tag>;
      },
    },
  ];

  return (
    <div>
      {overview.warning?.active && (
        <Alert
          type="warning" showIcon icon={<WarningOutlined />} style={{ marginBottom: 16 }}
          message="需量逼近合同需量"
          description={overview.warning.message}
        />
      )}

      {noData && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={overview.message || '暂无需量数据'} />
      )}

      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={`当月最大需量（${overview.month}）`}
              value={overview.max_demand_kw ?? '-'} suffix="kW" precision={1}
              valueStyle={{ color: ratioColor }}
            />
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
              发生时刻: {overview.occurred_at ? new Date(overview.occurred_at).toLocaleString('zh-CN', { hour12: false }) : '-'}
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="合同需量" value={overview.config.contract_kw || '-'} suffix="kW" />
            <div style={{ marginTop: 8 }}>
              <Progress percent={usagePercent} size="small" strokeColor={ratioColor} format={(p) => `占比 ${p}%`} />
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="当前滑窗需量" value={overview.current_demand_kw ?? '-'} suffix="kW" precision={1} />
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
              滑窗 {overview.config.window_minutes} 分钟 · 预警线 {overview.config.warn_ratio * 100}%
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="本月基本电费估算"
              value={overview.basic_fee_estimate_yuan ?? '-'} suffix="元" precision={0}
              valueStyle={{ color: '#fa8c16' }}
            />
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{overview.fee_note || ''}</div>
          </Card>
        </Col>
      </Row>

      <Card
        title="当日需量曲线" size="small" style={{ marginTop: 16 }}
        extra={user.role === 'admin' && <Button icon={<SettingOutlined />} onClick={openSettings}>需量设置</Button>}
      >
        {curve?.points?.length
          ? <ReactEChartsCore option={curveOption(curve)} style={{ height: 300 }} />
          : <Empty description="当日暂无功率遥测数据" />}
        <div style={{ fontSize: 12, color: '#999' }}>{curve?.calc_note}</div>
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={14}>
          <Card title="近6月最大需量趋势" size="small">
            {trend?.list?.some((m) => m.has_data)
              ? <ReactEChartsCore option={trendOption(trend)} style={{ height: 260 }} />
              : <Empty description="暂无历史需量数据" />}
          </Card>
        </Col>
        <Col span={10}>
          <Card title={<span><ThunderboltOutlined /> 储能压需量建议</span>} size="small">
            {suggestion ? (
              <>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="建议">{suggestion.advice}</Descriptions.Item>
                  {suggestion.suggested_discharge_kw > 0 && (
                    <Descriptions.Item label="建议放电功率">
                      <b style={{ color: '#fa8c16' }}>{suggestion.suggested_discharge_kw} kW</b>
                      （维持 ≥ {suggestion.sustain_minutes} 分钟）
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="储能额定功率/容量">
                    {suggestion.storage.rated_power_kw} kW / {suggestion.storage.rated_capacity_kwh} kWh
                  </Descriptions.Item>
                  <Descriptions.Item label="当前SOC">
                    {suggestion.storage.avg_soc != null ? `${suggestion.storage.avg_soc}%` : '暂无遥测'}
                  </Descriptions.Item>
                </Descriptions>
                <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                  {suggestion.estimation_note}<br />{suggestion.control_note}
                </div>
              </>
            ) : <Empty description="暂无建议" />}
          </Card>
        </Col>
      </Row>

      <Card title="历史需量明细" style={{ marginTop: 16 }} size="small">
        <Table
          columns={trendColumns}
          dataSource={(trend?.list || []).map((m) => ({ ...m, key: m.month }))}
          pagination={false}
          size="small"
        />
        <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>{overview.calc_note}</div>
      </Card>

      <Modal
        title="需量设置" open={settingsOpen}
        onCancel={() => setSettingsOpen(false)} onOk={saveSettings} okText="保存"
      >
        <Form form={settingsForm} layout="vertical">
          <Form.Item name="contract_kw" label="合同需量 (kW)" rules={[{ required: true, message: '请输入合同需量' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="window_minutes" label="需量滑窗 (分钟，国内口径通常15)" rules={[{ required: true, message: '请输入滑窗长度' }]}>
            <InputNumber min={5} max={60} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="warn_ratio" label="预警比例 (0.5~1，如0.9表示达到合同需量90%告警)" rules={[{ required: true, message: '请输入预警比例' }]}>
            <InputNumber min={0.5} max={1} step={0.05} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="price_per_kw" label="需量电价 (元/kW·月，按需量计费口径)" rules={[{ required: true, message: '请输入需量电价' }]}>
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="meter_ids" label="需量统计表计ID (逗号分隔，留空=自动识别市电/进线表)">
            <Input placeholder="如: 1,2" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
